import axios, { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';
import { store } from '../store/store';
import { clearAuth } from '../store/slices/authSlice';
import { addNotification } from '../store/slices/uiSlice';
import { logError } from '../utils/errorLogger';

// API Configuration
// Production-safe: Uses REACT_APP_API_URL if set, otherwise intelligent fallback
const getApiBaseUrl = () => {
  // If explicitly set in env, use that (production should always set this)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Fallback: Determine API URL based on current environment
  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol;
  const isProduction = currentProtocol === 'https:' || 
                       (!currentHost.includes('localhost') && 
                        !currentHost.includes('127.0.0.1'));
  
  if (isProduction) {
    // Production fallback: Use same hostname with /api path
    // This assumes frontend and backend are on the same domain
    // WARNING: This should not happen if REACT_APP_API_URL is properly configured
    console.warn('REACT_APP_API_URL not set in production! Using fallback. Please set REACT_APP_API_URL environment variable.');
    return `${currentProtocol}//${currentHost}/api`;
  }
  
  // Development: Prefer localhost for cookie compatibility
  // If frontend is on 127.0.0.1, still use localhost for API (they're the same machine)
  // This ensures cookies work properly with SameSite='Lax'
  const apiHost = currentHost === '127.0.0.1' ? 'localhost' : currentHost;
  const apiPort = '8000';
  
  // Use localhost for API (or current hostname if not 127.0.0.1)
  return `http://${apiHost}:${apiPort}/api`;
};

const API_BASE_URL = getApiBaseUrl();

// Cache configuration
interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

class APICache {
  private cache = new Map<string, CacheEntry>();
  
  set(key: string, data: any, ttl: number = 5 * 60 * 1000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }
  
  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }
  
  invalidate(pattern: string): void {
    Array.from(this.cache.keys()).forEach(key => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    });
  }
  
  clear(): void {
    this.cache.clear();
  }
}

// Enhanced API Client
class EnhancedAPIClient {
  private client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 120000, // 120 seconds (2 minutes) timeout for large file uploads
    withCredentials: true, // Enable cookies to be sent with requests
  });
  
  private cache = new APICache();
  private requestQueue: Map<string, Promise<any>> = new Map();
  
  constructor() {
    this.setupInterceptors();
  }
  
  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Ensure cookies are sent with all requests
        config.withCredentials = true;
        
        // Skip ngrok browser warning page (only when using ngrok)
        if (API_BASE_URL.includes('ngrok')) {
          config.headers['ngrok-skip-browser-warning'] = 'true';
        }
        
        // Note: Tokens are now in httpOnly cookies, which are sent automatically
        // Authorization header is no longer needed for cookie-based authentication
        
        // Add request ID for tracking - TEMPORARILY DISABLED due to CORS issues
        // config.headers['X-Request-ID'] = this.generateRequestId();
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    // Response interceptor with retry logic
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        
        // Handle CORS errors
        if (this.isCORSError(error)) {
          const corsError = new Error(
            'CORS Error: Unable to connect to the server. ' +
            'Please check that CORS is properly configured and the frontend URL is allowed.'
          );
          logError('CORS Error detected', {
            message: error.message,
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            statusText: error.response?.statusText,
          });
          store.dispatch(addNotification({
            type: 'error',
            title: 'Connection Error',
            message: 'Unable to connect to the server. Please check your network connection and try again.',
          }));
          return Promise.reject(corsError);
        }
        
        // Handle 401 (Unauthorized) with token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          return this.handleTokenRefresh(originalRequest);
        }
        
        // Handle network errors with retry
        if (this.isNetworkError(error) && !originalRequest._retryCount) {
          return this.handleRetry(originalRequest, error);
        }
        
        // Don't log 400 validation errors - they're handled by the form and don't need console noise
        // Only log unexpected errors (500+) or errors that aren't validation errors
        const status = error.response?.status;
        const responseData = error.response?.data as any;
        
        // Check if this is a validation error (400 with field-specific errors)
        const isValidationError = status === 400 && (
          responseData?.error?.type === 'ValidationError' ||
          responseData?.error?.details ||
          (responseData?.error && typeof responseData.error === 'object' && 
           (responseData.error.email || responseData.error.phone || responseData.error.password)) ||
          (responseData && typeof responseData === 'object' && 
           !responseData.success && 
           (responseData.email || responseData.phone || responseData.password))
        );
        
        // Check if this is an expected 404 (e.g., no active pricing configuration)
        const url = error.config?.url || '';
        const isExpected404 = status === 404 && (
          url.includes('/billing/pricing/current/') ||
          url.includes('/billing/pricing/current') ||
          url.includes('billing/pricing/current')
        );
        
        if (!isValidationError && !isExpected404) {
          // Log error (sanitized) for non-validation errors
          logError(`API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error);
        }
        
        // Handle other errors (but don't show notifications for validation errors or expected 404s)
        if (!isValidationError && !isExpected404) {
          this.handleApiError(error);
        }
        return Promise.reject(error);
      }
    );
  }
  
  private async handleTokenRefresh(originalRequest: any): Promise<AxiosResponse> {
    originalRequest._retry = true;
    
    try {
      // Refresh token is now in httpOnly cookie, so we just call the refresh endpoint
      // The backend will read the refresh token from the cookie
      await axios.post(
        `${API_BASE_URL}/auth/refresh/`,
        {},
        { withCredentials: true } // Ensure cookies are sent
      );
      
      // New access token is now in httpOnly cookie
      // We don't need to store it in Redux anymore
      // Just retry the original request - cookies will be sent automatically
      originalRequest.withCredentials = true;
      return this.client(originalRequest);
      
    } catch (refreshError) {
      // Check if we're currently authenticating or if this is an auth-related request
      const authState = store.getState().auth;
      const isAuthRequest = originalRequest.url?.includes('/auth/') || false;
      
      // Don't show "Session Expired" notification during login flow
      if (!authState.loading && !isAuthRequest) {
        store.dispatch(clearAuth());
        store.dispatch(addNotification({
          type: 'error',
          title: 'Session Expired',
          message: 'Please log in again',
        }));
      }
      throw refreshError;
    }
  }
  
  private async handleRetry(originalRequest: any, error: AxiosError): Promise<AxiosResponse> {
    const retryCount = originalRequest._retryCount || 0;
    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Exponential backoff
    
    if (retryCount < maxRetries) {
      originalRequest._retryCount = retryCount + 1;
      
      await this.delay(retryDelay);
      
      store.dispatch(addNotification({
        type: 'warning',
        title: 'Connection Issue',
        message: `Retrying request... (${retryCount + 1}/${maxRetries})`,
      }));
      
      return this.client(originalRequest);
    }
    
    throw error;
  }
  
  private isNetworkError(error: AxiosError): boolean {
    return !error.response && (
      error.code === 'NETWORK_ERROR' ||
      error.code === 'ECONNABORTED' ||
      error.message.includes('Network Error')
    );
  }
  
  private isCORSError(error: AxiosError): boolean {
    // CORS errors typically have no response and specific error messages
    if (!error.response) {
      const message = error.message?.toLowerCase() || '';
      return (
        message.includes('cors') ||
        message.includes('cross-origin') ||
        message.includes('access-control') ||
        error.code === 'ERR_NETWORK' ||
        (error.code === undefined && message.includes('network'))
      );
    }
    
    // Status 0 can indicate CORS/network (no real response); 403 is permission denied, not CORS
    if (error.response.status === 0) {
      const headers = error.response.headers || {};
      if (!headers['access-control-allow-origin']) {
        return true;
      }
    }
    
    return false;
  }
  
  private handleApiError(error: AxiosError): void {
    const status = error.response?.status;
    const message = (error.response?.data as any)?.message || error.message;
    
    // Handle network errors (no response from server)
    if (!status && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')) {
      // Don't show notification for network errors - let the calling component handle it
      return;
    }
    
    switch (status) {
      case 400:
        store.dispatch(addNotification({
          type: 'error',
          title: 'Bad Request',
          message: 'Please check your input and try again',
        }));
        break;
      case 403:
        store.dispatch(addNotification({
          type: 'error',
          title: 'Access Denied',
          message: 'You do not have permission to perform this action',
        }));
        break;
      case 404:
        store.dispatch(addNotification({
          type: 'error',
          title: 'Not Found',
          message: 'The requested resource was not found',
        }));
        break;
      case 429:
        store.dispatch(addNotification({
          type: 'warning',
          title: 'Too Many Requests',
          message: 'Please wait a moment before trying again',
        }));
        break;
      case 500:
        store.dispatch(addNotification({
          type: 'error',
          title: 'Server Error',
          message: 'Something went wrong on our end. Please try again later',
        }));
        break;
      default:
        if (status && status >= 400) {
          store.dispatch(addNotification({
            type: 'error',
            title: 'Request Failed',
            message: message || 'An unexpected error occurred',
          }));
        }
    }
  }
  
  private generateRequestId(): string {
    return Math.random().toString(36).substring(2, 15);
  }
  
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  // Public methods with caching support
  async get<T = any>(
    url: string, 
    config?: AxiosRequestConfig & { cache?: boolean; ttl?: number }
  ): Promise<AxiosResponse<T>> {
    const cacheKey = `GET:${url}:${JSON.stringify(config?.params || {})}`;
    
    if (config?.cache !== false) {
      const cached = this.cache.get(cacheKey);
      if (cached) {
        return { data: cached } as AxiosResponse<T>;
      }
    }
    
    const response = await this.client.get<T>(url, config);
    
    if (config?.cache !== false) {
      this.cache.set(cacheKey, response.data, config?.ttl);
    }
    
    return response;
  }
  
  async post<T = any>(
    url: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const response = await this.client.post<T>(url, data, config);
    
    // Invalidate related cache entries
    this.cache.invalidate(url.split('/')[1] || url);
    
    return response;
  }
  
  async put<T = any>(
    url: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const response = await this.client.put<T>(url, data, config);
    
    // Invalidate related cache entries
    this.cache.invalidate(url.split('/')[1] || url);
    
    return response;
  }
  
  async patch<T = any>(
    url: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const response = await this.client.patch<T>(url, data, config);
    
    // Invalidate related cache entries
    this.cache.invalidate(url.split('/')[1] || url);
    
    return response;
  }
  
  async delete<T = any>(
    url: string, 
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const response = await this.client.delete<T>(url, config);
    
    // Invalidate related cache entries
    this.cache.invalidate(url.split('/')[1] || url);
    
    return response;
  }
  
  // Optimistic update support - simplified version
  async optimisticUpdate<T = any>(
    url: string,
    data: any,
    optimisticData: T,
    cacheKey: string
  ): Promise<AxiosResponse<T>> {
    // Immediately update cache with optimistic data
    this.cache.set(cacheKey, optimisticData, 60000); // 1 minute TTL
    
    try {
      const response = await this.put<T>(url, data);
      
      // Update cache with real response
      this.cache.set(cacheKey, response.data, 300000); // 5 minutes TTL
      
      return response;
    } catch (error) {
      // Revert optimistic update on failure
      this.cache.invalidate(cacheKey.split(':')[0]);
      throw error;
    }
  }
  
  // Batch requests
  async batch(requests: Array<{ method: string; url: string; data?: any }>): Promise<any[]> {
    const promises = requests.map(req => {
      switch (req.method.toLowerCase()) {
        case 'get':
          return this.get(req.url);
        case 'post':
          return this.post(req.url, req.data);
        case 'put':
          return this.put(req.url, req.data);
        case 'patch':
          return this.patch(req.url, req.data);
        case 'delete':
          return this.delete(req.url);
        default:
          throw new Error(`Unsupported method: ${req.method}`);
      }
    });
    
    return Promise.allSettled(promises);
  }
  
  // Request deduplication
  async dedupedRequest<T = any>(
    key: string,
    requestFn: () => Promise<AxiosResponse<T>>
  ): Promise<AxiosResponse<T>> {
    if (this.requestQueue.has(key)) {
      return this.requestQueue.get(key);
    }
    
    const promise = requestFn().finally(() => {
      this.requestQueue.delete(key);
    });
    
    this.requestQueue.set(key, promise);
    return promise;
  }
  
  // Cache management
  invalidateCache(pattern?: string): void {
    if (pattern) {
      this.cache.invalidate(pattern);
    } else {
      this.cache.clear();
    }
  }
  
  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.get('/health/', { cache: false });
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const apiClient = new EnhancedAPIClient();
export default apiClient;
